"use client";

import { useEffect, useLayoutEffect, useRef } from "react";

import { useStudio } from "@/contexts/studio-context";

/**
 * Le tick que le studio dessine, image par image.
 *
 * ## Pourquoi un rappel et non une valeur
 *
 * Une valeur rendue serait un état React à soixante images par seconde, donc
 * un rendu complet de l'écran par image. Ce qui lit cette position doit
 * l'**écrire** là où elle se voit — un `transform`, une largeur — et le hook
 * ne rend donc rien. C'est le motif du remplissage de `useHarmonyCursor`,
 * appliqué à la position elle-même.
 *
 * ## Aucune boucle nouvelle
 *
 * Le rappel est appelé depuis le ticker Pixi du roll, par `Studio.onFrame`.
 * C'est la même boucle qui dessine les notes, gouvernée par le rendu à la
 * demande : elle tourne quand le transport joue, quand un geste de navigation
 * est en cours, quand une animation du roll court, et elle se parke sinon.
 * Une vue qui ouvrirait sa propre `requestAnimationFrame` pour suivre le roll
 * rallumerait exactement ce que ce gouverneur a été écrit pour éteindre.
 *
 * ## Et une pose à chaque rendu, parce que le ticker se parke
 *
 * Au repos il n'y a plus d'image, donc plus personne pour replacer un nœud que
 * React vient de remonter : le ruban reviendrait à zéro au premier rendu venu.
 * Le rappel est donc aussi appelé après chaque rendu, avec la position lue à
 * l'instant. C'est une lecture, pas une horloge.
 */
export function useStudioTick(onTick: (tick: number) => void): void {
  const { studio } = useStudio();

  /*
    Le rappel change d'identité à chaque rendu : il se referme sur la géométrie
    du dessin, qui se recalcule. Le garder dans une ref plutôt que dans les
    dépendances évite de défaire et refaire l'abonnement au ticker à chaque
    fois, pour un abonnement qui doit précisément survivre aux rendus.
  */
  const latest = useRef(onTick);

  useLayoutEffect(() => {
    latest.current = onTick;

    if (studio) {
      onTick(studio.getEffectiveTick());
    }
  });

  useEffect(() => {
    if (!studio) {
      return;
    }

    return studio.onFrame((tick) => latest.current(tick));
  }, [studio]);
}
